import React from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";

import { PrimaryButton } from "@components/Button";
import Card from "@components/Card";
import Div from "@components/Div";
import { H3, H5 } from "@components/Heading";
import Icon from "@components/Icon";
import { Text } from "@components/Text";

import {
  ProvideConsentQuestionDialog,
  ProvideConsentSuccessDialog,
} from "./components";
import { useProvideConsentCard } from "./ProvideConsentCard.hooks";

const ProvideConsentCard = ({
  companyName,
  expiresInDays,
  consentStatus,
  onProvideConsent,
}) => {
  const { formatMessage, messages } = useIntl();
  const {
    isQuestionDialogVisible,
    isSuccessDialogVisible,
    handleOpenQuestionDialog,
    handleCloseQuestionDialog,
    handleCloseSuccessDialog,
    handleUpdateConsent,
    isPendingConsent,
    IconDetails,
    TextDetails,
  } = useProvideConsentCard({ consentStatus, onProvideConsent });

  return (
    <Card borderRadius="10px">
      {isQuestionDialogVisible && (
        <ProvideConsentQuestionDialog
          onClose={handleCloseQuestionDialog}
          onSubmit={handleUpdateConsent}
        />
      )}

      {isSuccessDialogVisible && (
        <ProvideConsentSuccessDialog
          companyName={companyName}
          onClose={handleCloseSuccessDialog}
        />
      )}

      <Div
        width={1}
        px="24px"
        py={3}
        display="flex"
        flexDirection={["column", "column", "row"]}
        gridGap={3}
        alignItems={["flex-start", "flex-start", "center"]}
        justifyContent="space-between"
      >
        <Div
          display="flex"
          flexDirection={["column", "column", "row"]}
          gridGap="10px"
          alignItems="center"
        >
          <Div height="100%" alignSelf="start" pt={1}>
            <Icon
              name={IconDetails?.name}
              bg={`${IconDetails?.color} !important`}
              borderRadius="50%"
              p={1}
              color={`${IconDetails?.textColor} !important`}
            />
          </Div>

          <Div display="flex" flexDirection="column" gridGap={1}>
            <H3>{messages[TextDetails?.titleKey]}</H3>

            <Text>
              {messages[TextDetails?.descriptionKey]?.replace?.(
                "{companyName}",
                companyName
              )}
            </Text>

            {isPendingConsent && (
              <H5>
                {`${formatMessage({ id: "label_consent_expires_in" })} ${expiresInDays} ${formatMessage({ id: "label_days" })}`}
              </H5>
            )}
          </Div>
        </Div>

        {isPendingConsent && (
          <PrimaryButton
            whiteSpace="nowrap"
            label={messages.consent_title_share}
            onClick={handleOpenQuestionDialog}
          />
        )}
      </Div>
    </Card>
  );
};

ProvideConsentCard.propTypes = {
  companyName: PropTypes.string.isRequired,
  expiresInDays: PropTypes.number,
  consentStatus: PropTypes.string,
  onProvideConsent: PropTypes.func,
};

export default ProvideConsentCard;
